import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getDonors, getDonorView } from './api';

const DonorDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [donorsData, donorViewData] = await Promise.all([getDonors(), getDonorView()]);
        // Ensure data is an array
        const donors = Array.isArray(donorsData) ? donorsData : (donorsData?.donors || donorsData?.data || []);
        const mothers = donorViewData.donor_view || [];

        // Donor metrics
        const totalDonors = donors.length;
        const donorsWithMothers = donors.filter(d => d.mother_id !== null).length;
        const countryCounts = donors.reduce((acc, d) => {
          acc[d.country_of_residence] = (acc[d.country_of_residence] || 0) + 1;
          return acc;
        }, {});
        const countryWithHighestDonors = Object.entries(countryCounts).reduce((max, curr) =>
          (max[1] || 0) > (curr[1] || 0) ? max : curr, ['N/A', 0])[0];
        const topDonors = donors
          .sort((a, b) => (b.total_contributions || 0) - (a.total_contributions || 0))
          .map(d => ({
            id: d.donor_id,
            avatar: `/avatars/${d.donor_id}.png`,
            name: `${d.first_name} ${d.surname}`,
            country: d.country_of_residence,
            totalDonations: d.total_contributions || 0,
          }));

        // Donation and savings metrics from donor_view
        const allMonthlyData = mothers.flatMap(m => Object.entries(m.monthly_data || {}).map(([month, data]) => ({
          month,
          amount: (data.donor_contribution || 0) + (data.user_savings || 0),
        })));
        const totalDonations = allMonthlyData.reduce((sum, m) => sum + m.amount, 0);
        const monthlyDonations = Array.from(new Set(allMonthlyData)).sort((a, b) => a.month.localeCompare(b.month));
        const mothersWithDonors = mothers.length;

        // Country donation percentages (simplified placeholder)
        const countryDonationPercentages = { [countryWithHighestDonors]: 100 };

        setDashboardData({
          totalDonors,
          donorsWithMothers,
          totalDonations,
          countryWithHighestDonors,
          monthlyDonations,
          countryDonationPercentages,
          topDonors,
          mothersWithDonors,
        });
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>;

  const {
    totalDonors,
    donorsWithMothers,
    totalDonations,
    countryWithHighestDonors,
    monthlyDonations,
    countryDonationPercentages,
    topDonors,
    mothersWithDonors,
  } = dashboardData;

  const barGraphOptions = {
    chart: { type: 'column', height: isMobile ? 200 : 300 },
    title: { text: 'Total Donations per Month', style: { color: '#1a2e5c' } },
    xAxis: { categories: monthlyDonations.map(m => m.month.split('-')[1]) }, // Show only month (e.g., "07")
    yAxis: { title: { text: 'Donations ($)', style: { color: '#1a2e5c' } } },
    series: [{ name: 'Donations', data: monthlyDonations.map(m => m.amount), color: '#ff6f61' }],
    credits: { enabled: false },
    plotOptions: { column: { dataLabels: { enabled: true, style: { color: '#333' } } } },
  };

  const donutChartOptions = {
    chart: { type: 'pie', height: isMobile ? 200 : 300 },
    title: { text: 'Donation Percentages by Country', style: { color: '#1a2e5c' } },
    series: [{
      name: 'Percentage',
      data: Object.entries(countryDonationPercentages).map(([name, y]) => ({ name, y })),
      dataLabels: { enabled: true, format: '{point.name}: {point.percentage:.1f}%', style: { color: '#333' } },
      colors: ['#2a9d8f', '#e76f51', '#4a90e2'],
    }],
    credits: { enabled: false },
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: 'linear-gradient(135deg, #f5f5f5, #e0e7ff)', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#1a2e5c', fontWeight: 'bold', textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem' }}>
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: 2, 
        mb: 4, 
        width: '100%',
        flexWrap: 'wrap'
      }}>
        <StatCard 
          icon={<PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
          title="Total Donors" 
          value={totalDonors} 
          color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
          description="Registered worldwide"
        />
        <StatCard 
          icon={<PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
          title="Donors with Mothers" 
          value={donorsWithMothers} 
          color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
          description="Supporting mothers"
        />
        <StatCard 
          icon={<AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
          title="Total Donations" 
          value={`$${totalDonations.toLocaleString()}`} 
          color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
          description="From all donors"
        />
        <StatCard 
          icon={<LocationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
          title="Top Donor Country" 
          value={countryWithHighestDonors} 
          color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
          description="Leading contributions"
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3, mb: 4 }}>
        <Paper sx={{ p: 3, borderRadius: 3, flex: 1, background: '#f92955', color: '#FFFFFF' }}>
          <HighchartsReact highcharts={Highcharts} options={barGraphOptions} />
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 3, flex: 1, background: '#f92955', color: '#FFFFFF' }}>
          <HighchartsReact highcharts={Highcharts} options={donutChartOptions} />
        </Paper>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, background: '#f92955', color: '#FFFFFF' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1a2e5c' }}>Top 10 Donors</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ color: '#1a2e5c', fontWeight: 'bold' }}><PersonIcon /></TableCell>
                  <TableCell sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: '#1a2e5c', fontWeight: 'bold' }}><LocationOnIcon /></TableCell>
                  <TableCell sx={{ color: '#1a2e5c', fontWeight: 'bold' }}><AttachMoneyIcon /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topDonors.slice(0, 10).map((donor, index) => (
                  <TableRow key={donor.id || index} sx={{ '&:hover': { backgroundColor: '#f0f4f8' } }}>
                    <TableCell>{index + 1} {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''}</TableCell>
                    <TableCell><Avatar src={donor.avatar || '/default-avatar.png'} sx={{ width: 40, height: 40 }} /></TableCell>
                    <TableCell>{donor.name || 'N/A'}</TableCell>
                    <TableCell>{donor.country || 'N/A'}</TableCell>
                    <TableCell>${donor.totalDonations.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3, background: '#f92955', color: '#FFFFFF', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#1a2e5c' }}>Mothers with Donors</Typography>
            <Typography variant="h3" color="primary">{mothersWithDonors}</Typography>
            <PersonIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mt: 2 }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const StatCard = ({ icon, title, value, color, description }) => {
  return (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default DonorDashboard;